import type { IdentificationDocument, IdentityDetail } from './types'

export interface IdentityScoreResult {
  score: number
  maxScore: number
  breakdown: {
    label: string
    achieved: boolean
    points: number
  }[]
}

export function calculateIdentityScore(
  identity: IdentityDetail,
  documents: IdentificationDocument[],
  t: (key: string) => string
): IdentityScoreResult {
  const breakdown: { label: string; achieved: boolean; points: number }[] = []
  let score = 0

  // Contact info counts inline Person properties AND linked Email/Phone nodes
  // (HAS_EMAIL / HAS_PHONE relationships, hydrated by the backend).
  const linkedEmailCount = identity.emails?.length ?? 0
  const linkedPhoneCount = identity.phones?.length ?? 0
  const hasEmail = !!(identity.email || identity.properties?.email) || linkedEmailCount > 0
  const hasPhone =
    !!(identity.phone || identity.properties?.phone || identity.properties?.telephone) ||
    linkedPhoneCount > 0
  const hasContact = hasEmail || hasPhone

  // Check for full name (first + last name)
  const hasFullName =
    !!(identity.firstName && identity.lastName) ||
    !!(identity.name && identity.name.trim().split(' ').length >= 2)

  // Check for address
  const hasAddress = !!identity.address

  // Count verified documents (with or without photos)
  const verifiedDocs = documents.filter(doc => doc.isVerified).length

  // Level 1: Full name present (+1)
  breakdown.push({
    label: t('identity.detail.score.fullName'),
    achieved: hasFullName,
    points: 1,
  })
  if (hasFullName) score += 1

  // Level 2: Contact info (+1)
  breakdown.push({
    label: t('identity.detail.score.contact'),
    achieved: hasContact,
    points: 1,
  })
  if (hasContact) score += 1

  // Level 3: Address on record (+1)
  breakdown.push({
    label: t('identity.detail.score.addressOnRecord'),
    achieved: hasAddress,
    points: 1,
  })
  if (hasAddress) score += 1

  // Level 4: At least 1 verified document (+1)
  const hasVerifiedDoc = verifiedDocs >= 1
  breakdown.push({
    label: t('identity.detail.score.verifiedDoc'),
    achieved: hasVerifiedDoc,
    points: 1,
  })
  if (hasVerifiedDoc) score += 1

  // Level 5: 2+ verified documents (+1)
  const hasMultipleVerified = verifiedDocs >= 2
  breakdown.push({
    label: t('identity.detail.score.multipleVerified'),
    achieved: hasMultipleVerified,
    points: 1,
  })
  if (hasMultipleVerified) score += 1

  return { score, maxScore: 5, breakdown }
}

export function getScoreLabel(score: number, t: (key: string) => string): string {
  if (score <= 1) return t('identity.detail.score.labelLow')
  if (score === 2) return t('identity.detail.score.labelBasic')
  if (score === 3) return t('identity.detail.score.labelModerate')
  if (score === 4) return t('identity.detail.score.labelGood')
  return t('identity.detail.score.labelExcellent')
}
